import type { VaultItem } from '@heybesafe/shared';

/**
 * LastPass CSV column order. Must match the header parsed by
 * shared/src/importers/lastpass.tsx.
 */
const HEADER = ['url', 'username', 'password', 'totp', 'extra', 'name', 'grouping', 'fav'] as const;

const CRLF = '\r\n';

/**
 * Items eligible for the personal-logins export: type 'login' with no orgId.
 * The single source of truth — Settings shows this count; the exporter
 * writes these rows.
 */
export function personalLogins(items: VaultItem[]): VaultItem[] {
  return items.filter((i) => i.type === 'login' && !i.orgId);
}

/**
 * RFC-4180: wrap a field in double quotes if it contains a comma, double
 * quote, CR, or LF. Internal double quotes are doubled.
 */
function escapeCsv(field: string): string {
  if (/[",\r\n]/.test(field)) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}

function itemToRow(item: VaultItem): string[] {
  return [
    item.url ?? '',
    item.username ?? '',
    item.password ?? '',
    item.totp ?? '',
    item.content ?? '',
    item.name,
    item.group ?? '',
    item.favorite ? '1' : '0',
  ];
}

/**
 * Serialize personal login `VaultItem`s as a LastPass-format CSV string.
 *
 * - Filters to `type === 'login'` items with no `orgId` (personal only).
 * - Drops `tags`, `sensitive`, `importSource`, timestamps — no column for them.
 * - Emits the LastPass header even when there are zero rows.
 *
 * Returns the CSV string and the count of data rows (excluding the header).
 */
export function vaultItemsToLastpassCsv(
  items: VaultItem[],
): { csv: string; count: number } {
  const rows = personalLogins(items);
  const lines: string[] = [HEADER.join(',')];
  for (const item of rows) {
    lines.push(itemToRow(item).map(escapeCsv).join(','));
  }
  return {
    csv: lines.join(CRLF) + CRLF,
    count: rows.length,
  };
}
